import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Save } from "lucide-react";

interface ClientPreferences {
  desired_bedrooms: number | null;
  desired_bathrooms: number | null;
  max_rent: number | null;
  preferred_cities: string | null;
  preferred_states: string | null;
  additional_requirements: string | null;
}

interface ClientPreferencesFormProps {
  userId: string;
}

export const ClientPreferencesForm = ({ userId }: ClientPreferencesFormProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<ClientPreferences>({
    desired_bedrooms: null,
    desired_bathrooms: null,
    max_rent: null,
    preferred_cities: null,
    preferred_states: null,
    additional_requirements: null,
  });

  useEffect(() => {
    fetchPreferences();
  }, [userId]);

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from("client_profiles")
        .select("desired_bedrooms, desired_bathrooms, max_rent, preferred_cities, preferred_states, additional_requirements")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setPreferences(data);
      }
    } catch (error: any) {
      console.error("Error fetching preferences:", error);
      toast.error("Failed to load preferences");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("client_profiles")
        .update(preferences)
        .eq("user_id", userId);

      if (error) throw error;
      toast.success("Preferences saved successfully");
    } catch (error: any) {
      console.error("Error saving preferences:", error);
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Property Preferences</CardTitle>
        <CardDescription>
          Set your preferences to help us find the perfect property for you
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="bedrooms">Desired Bedrooms</Label>
            <Input
              id="bedrooms"
              type="number"
              min="0"
              placeholder="e.g., 2"
              value={preferences.desired_bedrooms ?? ""}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  desired_bedrooms: e.target.value ? parseInt(e.target.value) : null,
                })
              }
            />
          </div>
          <div>
            <Label htmlFor="bathrooms">Desired Bathrooms</Label>
            <Input
              id="bathrooms"
              type="number"
              min="0"
              step="0.5"
              placeholder="e.g., 1.5"
              value={preferences.desired_bathrooms ?? ""}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  desired_bathrooms: e.target.value ? parseFloat(e.target.value) : null,
                })
              }
            />
          </div>
          <div>
            <Label htmlFor="maxRent">Maximum Monthly Rent ($)</Label>
            <Input
              id="maxRent"
              type="number"
              min="0"
              placeholder="e.g., 2000"
              value={preferences.max_rent ?? ""}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  max_rent: e.target.value ? parseFloat(e.target.value) : null,
                })
              }
            />
          </div>
          <div>
            <Label htmlFor="cities">Preferred Cities (comma-separated)</Label>
            <Input
              id="cities"
              placeholder="e.g., Austin, Dallas"
              value={preferences.preferred_cities ?? ""}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  preferred_cities: e.target.value,
                })
              }
            />
          </div>
          <div>
            <Label htmlFor="states">Preferred States (comma-separated)</Label>
            <Input
              id="states"
              placeholder="e.g., TX, CA"
              value={preferences.preferred_states ?? ""}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  preferred_states: e.target.value,
                })
              }
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="requirements">Additional Requirements</Label>
            <Textarea
              id="requirements"
              placeholder="Any special requirements or preferences..."
              value={preferences.additional_requirements ?? ""}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  additional_requirements: e.target.value,
                })
              }
              rows={3}
            />
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="mt-4">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Preferences
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
